import { Api } from "../../index";
import { API_POST_POLICIES } from "../../Apis/ApiEndPoints";
import {
  IGetPoliciesRequestParams,
  IGetPolicesResponse,
  IPolicy,
} from "../../models/user/dashboard/Dashboard";
import { getCookie } from "../../utils/Utility";

export interface PolicyPickerOption {
  policyId: string;
  label: string;
  policyType: string;
}

export type VoicePolicyLine = "auto" | "homeowners" | "dwelling" | "unknown";

/** Normalize spoken policy type for account matching. */
export function normalizeVoicePolicyLine(voicePolicyType: string): VoicePolicyLine {
  const voice = voicePolicyType.trim().toLowerCase().replace(/\s+/g, " ");
  if (!voice) return "unknown";
  if (
    /\b(auto|vehicle|car|personal\s*auto|automobile|commercial)\b/.test(voice) ||
    voice === "pa"
  ) {
    return "auto";
  }
  if (/\b(dwelling|dp-?3)\b/.test(voice)) {
    return "dwelling";
  }
  if (
    /\b(home\s*owner|homeowner|home\s+owner|homeowners?|ho)\b/.test(voice) ||
    voice === "home"
  ) {
    return "homeowners";
  }
  if (/\b(property)\b/.test(voice)) {
    return "homeowners";
  }
  return "unknown";
}

function normalizePolicyToken(value: string): string {
  return value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

/** Drop duplicate rows (same policy number or id). */
export function dedupePolicies(policies: IPolicy[]): IPolicy[] {
  const seen = new Set<string>();
  const out: IPolicy[] = [];
  for (const policy of policies) {
    const no = normalizePolicyToken(policy.carrierPolicyNo || "");
    const key = no || (policy.policyId || "").trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(policy);
  }
  return out;
}

function isCommercialAutoPolicy(policy: IPolicy): boolean {
  const api = (policy.policyType || "").toLowerCase();
  const lob = (policy.lobCode || "").toUpperCase();
  return lob === "CA" || /\bcommercial\b/.test(api);
}

function isPersonalAutoPolicy(policy: IPolicy): boolean {
  if (isCommercialAutoPolicy(policy)) return false;
  const api = (policy.policyType || "").toLowerCase();
  const lob = (policy.lobCode || "").toUpperCase();
  if (lob === "PA") return true;
  if (/\bpersonal\s*auto\b/.test(api)) return true;
  if (/\bpersonal\b/.test(api) && /\bauto\b/.test(api)) return true;
  return false;
}

export function isDwellingPolicy(policy: IPolicy): boolean {
  const api = (policy.policyType || "").toLowerCase();
  const lob = (policy.lobCode || "").toUpperCase();
  return (
    lob === "DP" ||
    /\bdwelling\b/.test(api) ||
    /\bdp-?3\b/.test(api)
  );
}

export function isHomeownersPolicy(policy: IPolicy): boolean {
  if (isDwellingPolicy(policy)) return false;
  const api = (policy.policyType || "").toLowerCase();
  const lob = (policy.lobCode || "").toUpperCase();
  return (
    lob === "HO" ||
    /\bhomeowners?\b/.test(api) ||
    /\bhome\s*owner/.test(api) ||
    (/\bhome\b/.test(api) && !/\bdwelling\b/.test(api))
  );
}

export function policyMatchesVoiceLine(
  policy: IPolicy,
  line: VoicePolicyLine,
  voicePolicyType = ""
): boolean {
  const api = (policy.policyType || "").toLowerCase();
  const lob = (policy.lobCode || "").toUpperCase();
  const voice = voicePolicyType.trim().toLowerCase();
  if (line === "auto") {
    if (/\bcommercial\b/.test(voice)) {
      return isCommercialAutoPolicy(policy);
    }
    if (
      /\b(personal\s*auto|personal)\b/.test(voice) ||
      voice === "auto" ||
      voice === "personal auto"
    ) {
      return isPersonalAutoPolicy(policy);
    }
    if (isCommercialAutoPolicy(policy)) return false;
    return lob === "PA" || isPersonalAutoPolicy(policy);
  }
  if (line === "homeowners") {
    return isHomeownersPolicy(policy);
  }
  if (line === "dwelling") {
    return isDwellingPolicy(policy);
  }
  return false;
}

/** Maps voice-extracted labels to policies from the account API. */
export function filterPoliciesByVoiceType(
  policies: IPolicy[],
  voicePolicyType: string
): IPolicy[] {
  const deduped = dedupePolicies(policies);
  const line = normalizeVoicePolicyLine(voicePolicyType);
  if (line === "unknown") {
    const voice = voicePolicyType.trim().toLowerCase();
    return deduped.filter((policy) =>
      (policy.policyType || "").toLowerCase().includes(voice)
    );
  }
  return deduped.filter((policy) =>
    policyMatchesVoiceLine(policy, line, voicePolicyType)
  );
}

export function toPolicyPickerOptions(policies: IPolicy[]): PolicyPickerOption[] {
  return policies.map((policy) => ({
    policyId: policy.policyId,
    label: policy.carrierPolicyNo,
    policyType: policy.policyType,
  }));
}

export function getPoliciesRequestParams(
  fallback?: Partial<IGetPoliciesRequestParams>
): IGetPoliciesRequestParams | null {
  const customerId = getCookie("id") || fallback?.customerId;
  const carrier = getCookie("carrier") || fallback?.carrier || "";
  const system = getCookie("system") || fallback?.system || "";
  if (!customerId || !carrier || !system) return null;
  return { customerId, carrier, system };
}

/** POST dashboard/customer/policies — same as dashboard GetPoliciesData. */
export async function fetchCustomerPolicies(
  fallback?: Partial<IGetPoliciesRequestParams>
): Promise<IPolicy[]> {
  const requestParams = getPoliciesRequestParams(fallback);
  if (!requestParams) return [];

  const response = await Api.post<IGetPolicesResponse>(
    API_POST_POLICIES,
    requestParams
  );
  const body = response.data;
  return body?.data?.policyList ?? [];
}

export function readPolicyListFromResponse(
  response: IGetPolicesResponse | null | undefined
): IPolicy[] {
  return response?.data?.policyList ?? [];
}

export function displayPolicyTypeLabel(voicePolicyType: string): string {
  const line = normalizeVoicePolicyLine(voicePolicyType);
  if (line === "auto") return "Personal Auto";
  if (line === "dwelling") return "Dwelling";
  if (line === "homeowners") return "Homeowners";
  return voicePolicyType.trim();
}

/** Unique policy-type labels from the account API (never includes renters). */
export function getAccountPolicyTypeLabels(policies: IPolicy[]): string[] {
  const deduped = dedupePolicies(policies);
  const labels: string[] = [];
  if (deduped.some((p) => isPersonalAutoPolicy(p))) {
    labels.push("Personal Auto");
  }
  if (deduped.some((p) => isHomeownersPolicy(p))) {
    labels.push("Homeowners");
  }
  if (deduped.some((p) => isDwellingPolicy(p))) {
    labels.push("Dwelling");
  }
  const seen = new Set(labels);
  for (const policy of deduped) {
    const raw = (policy.policyType || "").trim();
    if (!raw || /\brenters?\b/i.test(raw)) continue;
    if (
      isPersonalAutoPolicy(policy) ||
      isHomeownersPolicy(policy) ||
      isDwellingPolicy(policy)
    ) {
      continue;
    }
    if (!seen.has(raw)) {
      seen.add(raw);
      labels.push(raw);
    }
  }
  return labels;
}

export function formatPolicyTypeQuestion(labels: string[]): string {
  if (!labels.length) {
    return "What type of policy are you calling about today?";
  }
  if (labels.length === 1) {
    return `What type of policy is this? I see ${labels[0]} on your account.`;
  }
  const joined = `${labels.slice(0, -1).join(", ")}, or ${labels[labels.length - 1]}`;
  return `What type of policy is this? On your account I see ${joined}.`;
}

export type PolicyResolveResult =
  | { kind: "single"; policy: IPolicy }
  | { kind: "multiple"; options: PolicyPickerOption[] }
  | { kind: "none" }
  | { kind: "not_ready" };

export function buildPolicyNumberPrompt(
  displayType: string,
  policyNumbers: string[]
): string {
  const count = policyNumbers.length;
  if (count < 2) return "";
  const nums =
    count === 2
      ? `${policyNumbers[0]} and ${policyNumbers[1]}`
      : policyNumbers.join(", ");
  return (
    `I see ${count} ${displayType} policies on your account: ${nums}. ` +
    "Which one is this for? Please say the first few or last few characters of the policy number."
  );
}

function extractPolicyPrefixSuffix(text: string): {
  prefix: string;
  suffix: string;
} {
  const lower = text.toLowerCase();
  let prefix = "";
  let suffix = "";
  const starts = lower.match(
    /\b(?:starts?|starting|begins?|beginning)\s+(?:with\s+)?([a-z0-9]{2,})/i
  );
  if (starts) prefix = normalizePolicyToken(starts[1]);
  const ends = lower.match(/\b(?:ends?|ending)\s+(?:with\s+)?([a-z0-9]{2,})/i);
  if (ends) suffix = normalizePolicyToken(ends[1]);
  return { prefix, suffix };
}

export function extractPolicyNumberFromText(text: string): string | null {
  const full = text.match(/\b([A-Z]{1,5}-?[A-Z0-9]{4,})\b/i);
  if (full) return full[1].toUpperCase();

  const near = text.match(
    /\b(?:auto|home|homeowners?|dwelling)\s+(?:insurance\s+)?policy\s+([A-Z0-9][A-Z0-9-]{3,})\b/i
  );
  if (near) return near[1].toUpperCase();

  return null;
}

export function policyCandidatesForVoiceType(
  policies: IPolicy[],
  voicePolicyType: string
): IPolicy[] {
  const matches = filterPoliciesByVoiceType(policies, voicePolicyType);
  return matches.length ? matches : dedupePolicies(policies);
}

export function resolvePoliciesForVoiceType(
  policies: IPolicy[],
  voicePolicyType: string
): PolicyResolveResult {
  if (!voicePolicyType.trim()) return { kind: "not_ready" };
  const deduped = dedupePolicies(policies);
  const matches = filterPoliciesByVoiceType(deduped, voicePolicyType);
  if (matches.length === 1) return { kind: "single", policy: matches[0] };
  if (matches.length > 1) {
    return { kind: "multiple", options: toPolicyPickerOptions(matches) };
  }
  return { kind: "none" };
}

export type PolicySpeechMatchResult =
  | { kind: "single"; policy: IPolicy }
  | { kind: "ambiguous" }
  | { kind: "none" };

function uniquePolicyMatches(
  policies: IPolicy[],
  predicate: (policyNo: string) => boolean
): IPolicy[] {
  return policies.filter((policy) =>
    predicate(normalizePolicyToken(policy.carrierPolicyNo))
  );
}

/**
 * Match caller speech to one policy when several share the same line.
 * Supports full number, "starts with HO0", "ends with 357", or a short token.
 */
export function matchPolicyBySpokenHint(
  policies: IPolicy[],
  spoken: string
): PolicySpeechMatchResult {
  if (!policies.length || !spoken.trim()) return { kind: "none" };

  const text = spoken.trim();
  const compact = normalizePolicyToken(text);

  const fullCode = text.match(/\b([A-Z]{1,5}-?[A-Z0-9]{4,})\b/i);
  if (fullCode) {
    const code = normalizePolicyToken(fullCode[1]);
    const exact = uniquePolicyMatches(policies, (pno) => pno === code);
    if (exact.length === 1) return { kind: "single", policy: exact[0] };
    if (exact.length > 1) return { kind: "ambiguous" };
  }

  const { prefix, suffix } = extractPolicyPrefixSuffix(text);

  if (!prefix && !suffix && compact.length >= 2 && compact.length <= 10) {
    const byPrefix = uniquePolicyMatches(policies, (pno) => pno.startsWith(compact));
    if (byPrefix.length === 1) return { kind: "single", policy: byPrefix[0] };
    const bySuffix = uniquePolicyMatches(policies, (pno) => pno.endsWith(compact));
    if (bySuffix.length === 1) return { kind: "single", policy: bySuffix[0] };
  }

  const digitRun = text.match(/\b(\d{2,8})\b/);
  if (digitRun) {
    const digits = normalizePolicyToken(digitRun[1]);
    if (digits.length >= 2) {
      const byContains = uniquePolicyMatches(policies, (pno) => pno.includes(digits));
      if (byContains.length === 1) return { kind: "single", policy: byContains[0] };
      if (byContains.length > 1) return { kind: "ambiguous" };
    }
  }

  if (!prefix && !suffix && compact.length >= 2 && compact.length <= 10) {
    const byPrefix = uniquePolicyMatches(policies, (pno) => pno.startsWith(compact));
    const bySuffix = uniquePolicyMatches(policies, (pno) => pno.endsWith(compact));
    if (byPrefix.length > 1 || bySuffix.length > 1) return { kind: "ambiguous" };
  }

  if (prefix) {
    const hits = uniquePolicyMatches(policies, (pno) => pno.startsWith(prefix!));
    if (hits.length === 1) return { kind: "single", policy: hits[0] };
    if (hits.length > 1) return { kind: "ambiguous" };
  }

  if (suffix) {
    const hits = uniquePolicyMatches(policies, (pno) => pno.endsWith(suffix!));
    if (hits.length === 1) return { kind: "single", policy: hits[0] };
    if (hits.length > 1) return { kind: "ambiguous" };
  }

  return { kind: "none" };
}
